import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { FormTemplate, ListTemplate } from '../types/formBuilder';
import { TemplateService } from '../services/templateService';
import CommonPageHeader from '../components/common/CommonPageHeader';
import SearchBar from '../components/common/SearchBar';
import LoadingScreen from '../components/common/LoadingScreen';
import ListBuilder from '../components/listBuilder/ListBuilder';
import TemplateList from '../components/formBuilder/TemplateList';
import ShareModal from '../components/formBuilder/ShareModal';
import ExpertModePanel from '../components/formBuilder/ExpertModePanel';
import { 
  PlusIcon, 
  RectangleStackIcon,
  TableCellsIcon,
  DocumentTextIcon,
  FunnelIcon,
  ArrowPathIcon,
  Squares2X2Icon,
  ListBulletIcon,
  TagIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';

type ViewMode = 'templates' | 'list-builder';
type TemplateType = 'form' | 'list';
type FilterType = 'all' | 'my-templates' | 'fillable';

const FormBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  
  // State
  const [viewMode, setViewMode] = useState<ViewMode>('templates');
  const [templateType, setTemplateType] = useState<TemplateType>('form');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [displayMode, setDisplayMode] = useState<'grid' | 'list'>('grid');
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | ListTemplate | null>(null);
  const [connectedFormTemplate, setConnectedFormTemplate] = useState<FormTemplate | null>(null);
  const [shareModal, setShareModal] = useState<{
    isOpen: boolean;
    template: FormTemplate | null;
  }>({
    isOpen: false,
    template: null
  });
  const [expertModeOpen, setExpertModeOpen] = useState(false);
  
  // Mock data - replace with actual API calls
  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  const [listTemplates, setListTemplates] = useState<ListTemplate[]>([]);

  // Permissions
  const canCreate = hasPermission('templates', 'admin');
  const canEdit = hasPermission('templates', 'admin');

  useEffect(() => {
    loadTemplates();
  }, [templateType]);

  // Set initial filter from URL params
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam && ['all', 'my-templates', 'fillable'].includes(filterParam)) {
      setFilterType(filterParam as FilterType);
    }
  }, [searchParams]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      if (templateType === 'form') {
        const templates = await TemplateService.getFormTemplates(user?.organizationId);
        setFormTemplates(templates);
      } else {
        const templates = await TemplateService.getListTemplates(user?.organizationId);
        setListTemplates(templates);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error(t('formBuilder.errors.loadFailed') || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    if (templateType === 'form') {
      navigate('/form-builder-editor');
    } else {
      setSelectedTemplate(null);
      setViewMode('list-builder');
    }
  };

  const handleEditTemplate = (template: FormTemplate | ListTemplate) => {
    if ('fields' in template) {
      navigate(`/form-builder-editor?templateId=${template.id}`);
    } else {
      setSelectedTemplate(template);
      setViewMode('list-builder');
    }
  };

  const handleFillForm = (template: FormTemplate) => {
    navigate(`/forms/${template.id}/fill`);
  };

  const handleShareForm = (template: FormTemplate) => {
    setShareModal({
      isOpen: true,
      template
    });
  };

  const handleCloseShareModal = () => {
    setShareModal({
      isOpen: false,
      template: null
    });
  };

  const handleUpdateTemplate = async (updates: Partial<FormTemplate>) => {
    if (!shareModal.template || !user?.organizationId) return;

    try {
      const updatedTemplate = {
        ...shareModal.template,
        ...updates,
        updatedAt: new Date(),
        version: (shareModal.template.version || 0) + 1
      };

      await TemplateService.updateFormTemplate(shareModal.template.id, updatedTemplate);
      
      // Update local state
      setFormTemplates(prev => 
        prev.map(t => t.id === shareModal.template!.id ? updatedTemplate : t)
      );

      // Update modal template
      setShareModal(prev => ({
        ...prev,
        template: updatedTemplate
      }));

      toast.success(t('formBuilder.messages.updateSuccess') || 'Template updated successfully');
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error(t('formBuilder.errors.updateFailed') || 'Failed to update template');
      throw error;
    }
  };

  const handleViewTemplate = async (template: FormTemplate | ListTemplate) => {
    if ('fields' in template) {
      // For forms, navigate to fill page (same as Fill button)
      navigate(`/forms/${template.id}/fill`);
    } else {
      // For lists, navigate to dedicated list page
      const listTemplate = template as ListTemplate;
      navigate(`/form-submissions-list/${listTemplate.id}`);
    }
  };

  const handleSaveTemplate = async (template: FormTemplate | ListTemplate) => {
    try {
      if ('fields' in template) {
        // Save form template
        const formTemplate = template as FormTemplate;
        
        // Add organizationId and createdBy if not present
        const createdByName = user?.displayName || 
                             user?.email?.split('@')[0] || 
                             `User ${user?.uid.slice(-6)}` || 
                             'Unknown User';
                             
        const templateWithMeta = {
          ...formTemplate,
          organizationId: formTemplate.organizationId || user?.organizationId || '',
          createdBy: formTemplate.createdBy || user?.uid || '',
          createdByName: formTemplate.createdByName || createdByName,
        };

        if (formTemplate.id) {
          // Update existing
          await TemplateService.updateFormTemplate(formTemplate.id, templateWithMeta);
          setFormTemplates(prev => prev.map(t => t.id === formTemplate.id ? { ...templateWithMeta, id: formTemplate.id } as FormTemplate : t));
          toast.success(t('formBuilder.messages.updateSuccess') || 'Form template updated successfully');
        } else {
          // Create new
          const newTemplate = await TemplateService.createFormTemplate(templateWithMeta);
          setFormTemplates(prev => [...prev, newTemplate]);
          
          // Create default list for the new form
          try {
            await TemplateService.createDefaultListForForm(newTemplate);
            toast.success(t('formBuilder.messages.createSuccessWithList') || 'Form template and default list created successfully');
          } catch (error) {
            console.error('Error creating default list:', error);
            toast.success(t('formBuilder.messages.createSuccess') || 'Form template created successfully');
            toast.error(t('formBuilder.messages.defaultListError') || 'Could not create default list');
          }
        }
      } else {
        // Save list template
        const listTemplate = template as ListTemplate;
        
        // Add organizationId and createdBy if not present
        const createdByName = user?.displayName || 
                             user?.email?.split('@')[0] || 
                             `User ${user?.uid.slice(-6)}` || 
                             'Unknown User';
                             
        const templateWithMeta = {
          ...listTemplate,
          organizationId: listTemplate.organizationId || user?.organizationId || '',
          createdBy: listTemplate.createdBy || user?.uid || '',
          createdByName: listTemplate.createdByName || createdByName,
        };

        if (listTemplate.id) {
          // Update existing
          await TemplateService.updateListTemplate(listTemplate.id, templateWithMeta);
          setListTemplates(prev => prev.map(t => t.id === listTemplate.id ? { ...templateWithMeta, id: listTemplate.id } as ListTemplate : t));
          toast.success(t('listBuilder.messages.updateSuccess') || 'List template updated successfully');
        } else {
          // Create new
          const newTemplate = await TemplateService.createListTemplate(templateWithMeta);
          setListTemplates(prev => [...prev, newTemplate]);
          toast.success(t('listBuilder.messages.createSuccess') || 'List template created successfully');
        }
      }
      
      setViewMode('templates');
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(t('formBuilder.errors.saveFailed') || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      if (templateType === 'form') {
        await TemplateService.deleteFormTemplate(templateId);
        setFormTemplates(prev => prev.filter(t => t.id !== templateId));
      } else {
        await TemplateService.deleteListTemplate(templateId);
        setListTemplates(prev => prev.filter(t => t.id !== templateId));
      }
      toast.success(t('formBuilder.messages.deleteSuccess') || 'Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(t('formBuilder.errors.deleteFailed') || 'Failed to delete template');
    }
  };

  const handleCloseBuilder = () => {
    setViewMode('templates');
    setSelectedTemplate(null);
    setConnectedFormTemplate(null);
  };

  // Form template exportálása
  const handleExportTemplate = (template: FormTemplate) => {
    try {
      const exportData = TemplateService.exportFormTemplate(template);
      const blob = new Blob([exportData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.name || 'form-template'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Az űrlap sablon sikeresen exportálva lett.');
    } catch (error) {
      console.error('Error exporting template:', error);
      toast.error('Hiba történt az exportálás során.');
    }
  };

  // Form template importálása
  const handleImportTemplate = async (importedTemplates: Partial<FormTemplate>[], organizationId: string, addSuffix: boolean = true) => {
    try {
      if (!user?.uid) {
        toast.error('Nincs megfelelő jogosultság az importáláshoz.');
        return;
      }

      const createdByName = user?.displayName || 
                           user?.email?.split('@')[0] || 
                           `User ${user?.uid.slice(-6)}` || 
                           'Unknown User';

      const newTemplates: FormTemplate[] = [];
      
      for (const importedTemplate of importedTemplates) {
        let templateName = importedTemplate.name;
        
        // Tisztítjuk az "(importált)" és "(importált 1)", "(importált 2)" stb. szövegeket
        if (templateName) {
          templateName = templateName.replace(/\s*\(importált\s*\d*\)\s*/g, '').trim();
        }
        
        // Csak akkor adjuk hozzá az (importált) utótagot, ha a felhasználó kéri
        if (addSuffix && templateName) {
          templateName = `${templateName} (importált)`;
        }

        const templateWithMeta: Omit<FormTemplate, 'id'> = {
          ...importedTemplate,
          name: templateName,
          organizationId: organizationId,
          createdBy: user.uid,
          createdByName,
          createdAt: new Date(),
          updatedAt: new Date(),
          version: 1,
          isActive: true,
          fields: importedTemplate.fields || [],
          sections: importedTemplate.sections || [],
          settings: {
            submitButtonText: 'Submit',
            cancelButtonText: 'Cancel',
            showProgressBar: false,
            allowDrafts: true,
            multiStep: false,
            ...importedTemplate.settings
          },
          permissions: {
            canView: [],
            canEdit: [],
            canSubmit: [],
            ...importedTemplate.permissions
          }
        } as Omit<FormTemplate, 'id'>;

        const newTemplate = await TemplateService.createFormTemplate(templateWithMeta);
        newTemplates.push(newTemplate);
      }

      setFormTemplates(prev => [...prev, ...newTemplates]);
      toast.success(`${newTemplates.length} űrlap sablon sikeresen importálva lett.`);
    } catch (error) {
      console.error('Error importing template:', error);
      toast.error(`Hiba történt az importálás során: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`);
    }
  };

  // Összes form template exportálása
  const handleExportAllTemplates = (templates: FormTemplate[]) => {
    try {
      const exportData = TemplateService.exportMultipleFormTemplates(templates);
      const blob = new Blob([exportData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `form-templates-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`${templates.length} űrlap sablon sikeresen exportálva lett.`);
    } catch (error) {
      console.error('Error exporting templates:', error);
      toast.error('Hiba történt az exportálás során.');
    }
  };


  const handleCreateListForForm = (formTemplate: FormTemplate) => {
    // Create a new list template connected to this form
    const newListTemplate: Partial<ListTemplate> = {
      name: `${formTemplate.name} - Custom List`,
      description: `Custom list view for ${formTemplate.name} form submissions`,
      formTemplateId: formTemplate.id,
      isDefaultList: false,
      dataSource: {
        type: 'form_submissions',
        formTemplateId: formTemplate.id
      },
      columns: [],
      filters: [],
      settings: {
        pageSize: 25,
        showSearch: true,
        showFilters: true,
        showExport: true,
        allowSelection: false,
        allowInlineEdit: false
      },
      permissions: {
        canView: formTemplate.permissions.canView,
        canEdit: formTemplate.permissions.canEdit,
        canDelete: formTemplate.permissions.canEdit // Use edit permission for delete
      },
      organizationId: formTemplate.organizationId,
      createdBy: formTemplate.createdBy,
      createdByName: formTemplate.createdByName,
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true
    };

    setSelectedTemplate(newListTemplate as ListTemplate);
    setConnectedFormTemplate(formTemplate); // Store the connected form
    setTemplateType('list');
    setViewMode('list-builder');
  };

  const handleViewFormLists = async (formTemplate: FormTemplate) => {
    try {
      setLoading(true);
      const formLists = await TemplateService.getListsForForm(formTemplate.id!);
      
      if (formLists.length === 0) {
        toast(t('formBuilder.messages.noListsForForm') || 'No lists found for this form. Create one first.', { icon: 'ℹ️' });
        handleCreateListForForm(formTemplate);
      } else {
        // Switch to lists view and show only this form's lists
        setTemplateType('list');
        // Navigate to the first list directly
        if (formLists.length === 1) {
          // If only one list, open it directly
          navigate(`/form-submissions-list/${formLists[0].id}`);
        } else {
          // If multiple lists, show them in the list view
          setListTemplates(formLists);
          toast.success(t('formBuilder.messages.listsLoaded') || `Found ${formLists.length} list(s) for this form`);
        }
      }
    } catch (error) {
      console.error('Error loading form lists:', error);
      toast.error(t('formBuilder.messages.loadListsError') || 'Failed to load lists for this form');
    } finally {
      setLoading(false);
    }
  };

  // Collect all unique tags from templates
  const allTags = Array.from(new Set(
    formTemplates.flatMap(t => t.tags || [])
  )).sort();

  // Filter templates based on search, filter type, and tags
  const filteredFormTemplates = formTemplates.filter(template => {
    // Search filter
    const matchesSearch = !searchTerm || (
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Filter type
    const matchesFilter = (() => {
      switch (filterType) {
        case 'my-templates':
          return template.createdBy === user?.uid;
        case 'fillable':
          return true; // All forms are fillable, could add permission check here
        case 'all':
        default:
          return true;
      }
    })();

    // Tag filter
    const matchesTags = selectedTags.length === 0 || 
      selectedTags.some(tag => template.tags?.includes(tag));

    return matchesSearch && matchesFilter && matchesTags;
  });

  const filteredListTemplates = listTemplates.filter(template => {
    // Search filter
    const matchesSearch = !searchTerm || (
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter type
    const matchesFilter = (() => {
      switch (filterType) {
        case 'my-templates':
          return template.createdBy === user?.uid;
        case 'fillable':
          return false; // Lists are not fillable
        case 'all':
        default:
          return true;
      }
    })();

    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return <LoadingScreen />;
  }


  if (viewMode === 'list-builder') {
    return (
      <ListBuilder
        isOpen={true}
        onClose={handleCloseBuilder}
        onSave={handleSaveTemplate}
        initialTemplate={selectedTemplate as ListTemplate}
        connectedForm={connectedFormTemplate || undefined}
      />
    );
  }


  return (
    <div className="space-y-6">
      {/* Page Header */}
      <CommonPageHeader
        title={t('formBuilder.title') || 'Forms & Lists Builder'}
        subtitle={t('formBuilder.subtitle') || 'Create custom forms for data collection and lists for data display'}
        icon={RectangleStackIcon}
      >
        <div className="flex items-center space-x-3">
          {user?.role === 'super_admin' && (
            <button
              onClick={() => setExpertModeOpen(true)}
              className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <span>🔧 Szakértő mód</span>
            </button>
          )}
          {canCreate && (
            <button
              onClick={handleCreateNew}
              className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              <span>{templateType === 'form' ? t('formBuilder.newForm') || 'New Form' : t('listBuilder.newList') || 'New List'}</span>
            </button>
          )}
        </div>
      </CommonPageHeader>

      {/* Quick Guide */}
      {(templateType === 'form' ? formTemplates.length === 0 : listTemplates.length === 0) && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              {templateType === 'form' ? (
                <DocumentTextIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              ) : (
                <TableCellsIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                {templateType === 'form' 
                  ? (t('formBuilder.quickGuide.formsTitle') || 'Create Your First Form')
                  : (t('listBuilder.quickGuide.listsTitle') || 'Create Your First List')
                }
              </h3>
              <p className="text-blue-700 dark:text-blue-300 mb-4">
                {templateType === 'form' 
                  ? (t('formBuilder.quickGuide.formsDescription') || 'Forms allow you to collect data from users. You can add various field types like text, email, dropdowns, and more.')
                  : (t('listBuilder.quickGuide.listsDescription') || 'Lists allow you to display collected data in organized tables. You can customize columns, add filters, and format data display.')
                }
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                  {templateType === 'form' ? '📝 Data Collection' : '📊 Data Display'}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                  {templateType === 'form' ? '🎨 Drag & Drop Builder' : '🔧 Column Customization'}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                  {templateType === 'form' ? '✅ Validation Rules' : '🔍 Filtering & Search'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            {/* Template Type Tabs */}
            {/* Listaépítő kivezetve – csak űrlapok */}

            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              {/* Filter Tabs */}
              <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setFilterType('all')}
                  className={`
                    px-3 py-1.5 rounded-md text-xs font-medium transition-colors
                    ${filterType === 'all'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }
                  `}
                >
                  {t('common.all') || 'All'}
                </button>
                <button
                  onClick={() => setFilterType('my-templates')}
                  className={`
                    px-3 py-1.5 rounded-md text-xs font-medium transition-colors
                    ${filterType === 'my-templates'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }
                  `}
                >
                  {t('myForms.title') || 'My Templates'}
                </button>
                {templateType === 'form' && (
                  <button
                    onClick={() => setFilterType('fillable')}
                    className={`
                      px-3 py-1.5 rounded-md text-xs font-medium transition-colors
                      ${filterType === 'fillable'
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('myForms.filters.available') || 'Fillable'}
                  </button>
                )}
              </div>

              {/* View Mode Toggle */}
              <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setDisplayMode('grid')}
                  className={`
                    px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center
                    ${displayMode === 'grid'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }
                  `}
                  title="Grid nézet"
                >
                  <Squares2X2Icon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDisplayMode('list')}
                  className={`
                    px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center
                    ${displayMode === 'list'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }
                  `}
                  title="Lista nézet"
                >
                  <ListBulletIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Search */}
              <div className="w-full sm:w-80">
                <SearchBar
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder={t('formBuilder.searchPlaceholder') || 'Search templates...'}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tag Filter */}
        {templateType === 'form' && allTags.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex-shrink-0">
                <TagIcon className="h-4 w-4" />
                <span>Tag-ek:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => {
                      setSelectedTags(prev => 
                        prev.includes(tag) 
                          ? prev.filter(t => t !== tag)
                          : [...prev, tag]
                      );
                    }}
                    className={`
                      inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-all
                      ${selectedTags.includes(tag)
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    {tag}
                    {selectedTags.includes(tag) && (
                      <XMarkIcon className="h-3 w-3 ml-1" />
                    )}
                  </button>
                ))}
                {selectedTags.length > 0 && (
                  <button
                    onClick={() => setSelectedTags([])}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                  >
                    <XMarkIcon className="h-3 w-3 mr-1" />
                    Összes törlése
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Template List */}
        <div className="p-4">
          <TemplateList
            templates={templateType === 'form' ? filteredFormTemplates : filteredListTemplates}
            templateType={templateType}
            displayMode={displayMode}
            onEdit={canEdit ? handleEditTemplate : undefined}
            onDelete={canEdit ? handleDeleteTemplate : undefined}
            onDuplicate={canCreate ? (template) => {
              const duplicated = {
                ...template,
                id: '',
                name: `${template.name} (Copy)`,
                createdAt: new Date(),
                updatedAt: new Date(),
                version: 1
              };
              handleEditTemplate(duplicated);
            } : undefined}
            onFill={templateType === 'form' ? handleFillForm : undefined}
            onShare={templateType === 'form' ? handleShareForm : undefined}
            onCreateListForForm={canCreate ? handleCreateListForForm : undefined}
            onViewFormLists={handleViewFormLists}
            onView={handleViewTemplate}
            onExport={templateType === 'form' ? handleExportTemplate : undefined}
            onImport={templateType === 'form' && canCreate ? handleImportTemplate : undefined}
            onExportAll={templateType === 'form' ? handleExportAllTemplates : undefined}
          />
        </div>
      </div>

      {/* Share Modal */}
      {shareModal.template && (
        <ShareModal
          isOpen={shareModal.isOpen}
          onClose={handleCloseShareModal}
          template={shareModal.template}
          onUpdateTemplate={handleUpdateTemplate}
        />
      )}

      {/* Expert Mode Panel */}
      <ExpertModePanel
        isOpen={expertModeOpen}
        onClose={() => setExpertModeOpen(false)}
      />
    </div>
  );
};

export default FormBuilderPage;
